/**
 * This module exports Scalyr-defined pure functions for processing logic related to time.
 */

import moment from 'moment-timezone';
import { isString } from 'lodash';

import * as K from 'common/constants';
import { Range, TimeRange } from 'common/types';
import { roundDownToSigFigs } from 'common/util/number';

// Time and date-related constants

// recognized duration strings
const DURATIONS: Record<K.DATE, string[]> = {} as Record<K.DATE, string[]>;
Object.values(K.DATE).forEach((key) => {
  DURATIONS[key as K.DATE] = [];
});
DURATIONS[K.DATE.SECOND] = ['second', 'sec'];
DURATIONS[K.DATE.MINUTE] = ['minute', 'min'];
DURATIONS[K.DATE.HOUR] = ['hour', 'hr'];
DURATIONS[K.DATE.DAY] = ['day'];
DURATIONS[K.DATE.WEEK] = ['week', 'wk'];
DURATIONS[K.DATE.MONTH] = ['month'];
DURATIONS[K.DATE.YEAR] = ['year', 'yr'];

// map duration string to its unit
const DURATION_UNIT: Record<string, K.DATE> = {};
Object.keys(DURATIONS).forEach((unit) => {
  const durations = DURATIONS[unit as K.DATE];
  durations.forEach((duration) => {
    DURATION_UNIT[duration] = unit as K.DATE;
    DURATION_UNIT[duration + 's'] = unit as K.DATE;
    if (duration !== K.DATE.MONTH) {
      DURATION_UNIT[duration.substring(0, 1)] = unit as K.DATE;
    }
  });
});

// ranges for validating values before using a Moment setter
const UNIT_RANGE: Record<K.DATE, Range> = {
  [K.DATE.MILLISECOND]: { min: 0, max: 999 },
  [K.DATE.SECOND]: { min: 0, max: 59 },
  [K.DATE.MINUTE]: { min: 0, max: 59 },
  [K.DATE.HOUR]: { min: 0, max: 23 },
  [K.DATE.WEEKDAY]: { min: -6, max: 6 },
  [K.DATE.DAY]: { min: 1, max: 31 },
  [K.DATE.WEEK]: { min: 0, max: 51 }, // not used
  [K.DATE.MONTH]: { min: 0, max: 11 },
  [K.DATE.YEAR]: { min: 2000, max: 2100 },
};

// milliseconds per time unit (second, minute, hour, day, week)
export const MS_PER_UNIT: Record<K.DATE, number> = {} as Record<K.DATE, number>;
Object.values(K.DATE).forEach((key) => {
  MS_PER_UNIT[key as K.DATE] = -1;
});
MS_PER_UNIT[K.DATE.SECOND] = 1000;
MS_PER_UNIT[K.DATE.MINUTE] = 1000 * 60;
MS_PER_UNIT[K.DATE.HOUR] = 1000 * 60 * 60;
MS_PER_UNIT[K.DATE.DAY] = 1000 * 60 * 60 * 24;
MS_PER_UNIT[K.DATE.WEEK] = 1000 * 60 * 60 * 24 * 7;
MS_PER_UNIT[K.DATE.MONTH] = 1000 * 60 * 60 * 24 * 30;
MS_PER_UNIT[K.DATE.YEAR] = 1000 * 60 * 60 * 24 * 365;

// name of date component/setter in Moment
const MOMENT_UNIT: Record<K.DATE, string> = {} as Record<K.DATE, string>;
Object.values(K.DATE).forEach((key) => {
  MOMENT_UNIT[key as K.DATE] = key;
});
MOMENT_UNIT[K.DATE.WEEKDAY] = 'day';
MOMENT_UNIT[K.DATE.DAY] = 'date';

const DAYS_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

// maps three-letter version of weekday (eg 'mon') to its zero-based position starting with Sunday
const DAY_NUM = DAYS_OF_WEEK.map((day) => day.substr(0, 3));

// lookup hash used to identify multiple forms of day of week, eg: wednesday wed. wed
const IS_DAY_OF_WEEK: Record<string, boolean> = {};
DAYS_OF_WEEK.forEach((day) => {
  IS_DAY_OF_WEEK[day] = true;
  IS_DAY_OF_WEEK[day.substr(0, 3)] = true;
  IS_DAY_OF_WEEK[day.substr(0, 3) + '.'] = true;
});

const MONTHS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
];

// lookup hash used to identify multiple forms of month, eg: march mar. mar
const IS_MONTH: Record<string, boolean> = {};
MONTHS.forEach((month) => {
  IS_MONTH[month] = true;
  IS_MONTH[month.substr(0, 3)] = true;
  IS_MONTH[month.substr(0, 3) + '.'] = true;
});

// maps three-letter version of month (eg 'sep') to its zero-based position
const MONTH_NUM = MONTHS.map((month) => month.substr(0, 3));

// strings for UTC time zone (offset of 0)
const OFFSETS = ['utc', 'gmt', 'z'];

// lookup hash used to identify UTC string
const IS_OFFSET: Record<string, boolean> = {};
OFFSETS.forEach((offset) => {
  IS_OFFSET[offset] = true;
});

const MAX_SECONDS_DURATION = 500;
const SIX_ZEROS = '000000';

export const REGEXP = {
  dayOfWeek: new RegExp(
    Object.keys(IS_DAY_OF_WEEK)
      .map((key) => key.replace('.', '\\.'))
      .join('|'),
  ),
  digits: /^\d+$/,
  duration: /^\+?([\d.]+)\s*([a-zA-Z]+)$/,
  monthDay: /(\d{1,4})(\/)(\d{1,4})/,
  relativeTimeColonSeparated: /(\d+:)+\d+(\.\d+)?\s*(am|pm)?/,
  timeMilitary: /(\d+:)+\d+/,
  timeRegular: /(\d{1,6})\s*(am|pm)/,
  yearMonthDay: /(\d{1,4})([/\-.])(\d{1,2})\2(\d{1,4})/,
};

/**
 * Wrapper around moment to set the timezone property if given
 *
 * @param timezone the timezone to use
 *
 * @returns wrapped instance of moment
 */
export const timezoneMoment =
  (timezone: string | null) =>
  (input?: moment.MomentInput, format?: moment.MomentFormatSpecification, strict?: boolean) =>
    timezone ? moment(input, format, strict).tz(timezone) : moment(input, format, strict);

/**
 * Get timezone offset from moment library based on the passed timezone
 *
 * @param timezone the timezone to use
 * @returns timezone offset
 */
export const getTimezoneOffset = (timezone: string | null) => {
  const zone = moment.tz.zone(timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
  return zone?.parse(Date.now());
};

/**
 * Returns true if the given time is an epoch second, millisecond or nanosecond time.
 * Please note:
 *  Scalyr web-app still manages all the epoch dates as numeric strings in memory.
 *  Please use this check only when working with visual representation of the dates (ie "pretty-printing")
 */
export const isEpochTime = (time: string | number): boolean => {
  time = String(time);
  return (time.length === 10 || time.length === 13 || time.length === 19) && REGEXP.digits.test(time);
};

/**
 * @param {Number} timeZoneOffset the UTC offset in minutes ( Can get this value from native
 *                                JS by using SomeDateObject.getTimezoneOffset(); )
 *
 * @returns {String}              The formatted UTC time zone offset string
 */
export const getFormattedTimeOffsetUTC = (timeZoneOffset = 0): string => {
  let formattedOffset = 'UTC+0';
  const timeZoneOffsetInHour = Math.floor(timeZoneOffset / 60);
  if (timeZoneOffsetInHour > 0) {
    formattedOffset = `UTC-${timeZoneOffsetInHour}`;
  } else if (timeZoneOffsetInHour < 0) {
    formattedOffset = `UTC+${Math.abs(timeZoneOffsetInHour)}`;
  }

  return formattedOffset;
};

/**
 * Gets the formatted date range text between two dates. The format used varies depending whether the two times
 * are in the same year or day.
 *
 * @param from           start date
 * @param to             end date
 * @param timezone       timezone to use, if null uses local timezone
 * @param prependDate    if true, prepend date
 * @param includeDay     if true, include day of week in date
 *
 * @returns {string}              The formatted date range.
 */
export const getDateRangeText = (
  from: Date | number,
  to: Date | number,
  timezone: string | null,
  prependDate?: boolean,
  includeDay?: boolean,
): string => {
  const fromDate = timezoneMoment(timezone)(from);
  const toDate = timezoneMoment(timezone)(to);

  const dateFormat = includeDay ? 'ddd MMM DD' : 'MMM DD';
  let fromFormat = prependDate ? `${dateFormat} · h:mm:ss` : 'h:mm:ss';
  let toFormat = 'h:mm:ss a';

  if (!fromDate.isSame(toDate, MOMENT_UNIT[K.DATE.YEAR] as moment.unitOfTime.StartOf)) {
    fromFormat = `${dateFormat}, YYYY · h:mm a`;
    toFormat = `${dateFormat}, YYYY · h:mm a`;
  } else if (
    !fromDate.isSame(toDate, MOMENT_UNIT[K.DATE.MONTH] as moment.unitOfTime.StartOf) ||
    !fromDate.isSame(toDate, MOMENT_UNIT[K.DATE.DAY] as moment.unitOfTime.StartOf)
  ) {
    fromFormat = `${dateFormat} · h:mm a`;
    toFormat = `${dateFormat} · h:mm a`;
  } else if (!fromDate.isSame(toDate, MOMENT_UNIT[K.DATE.HOUR] as moment.unitOfTime.StartOf)) {
    if (fromDate.hour() < 12 !== toDate.hour() < 12) {
      fromFormat = prependDate ? `${dateFormat} · h:mm a` : 'h:mm a';
      toFormat = 'h:mm a';
    } else {
      fromFormat = prependDate ? `${dateFormat} · h:mm` : 'h:mm';
      toFormat = 'h:mm a';
    }
  }

  return `${fromDate.format(fromFormat)} - ${toDate.format(toFormat)}`;
};

/**
 * @param date    timestamp date to be relative from.
 * @return string represents of how long ago it was.
 */
export function getRelativeFromNow(date?: number): string {
  return moment(date).fromNow();
}

type ParsedDateTime = {
  milliseconds: number;
  moment: moment.Moment;
  nanoseconds?: string;
  ref?: moment.Moment;
};

/**
 * Parse a string of user input into a date and time. Times are assumed to be in the past. Dates up to 30 days into the
 * future are supported. The momentjs library is used to do most of the heavy lifting. If parsing is successful, an
 * object containing the resulting moment and the date in milliseconds or nanoseconds is returned. That's necessary
 * because momentjs cannot handle nanoseconds. If we get a nanosecond timestamp, we return it as a string, because
 * as a number it's too big for JavaScript.
 *
 * Note that this function does not validate the input. Its goal is to convert the input to a usable date/time. If the
 * input is not recognizable as a date/time, a response for the current time will be returned.
 *
 * A reference time has two possible uses (otherwise it should be omitted):
 *   1. If the input is a relative duration (eg "+3 hours"), reference time is the start time.
 *   2. If input is a time with an hour < 12 (eg "3:45"), reference time is end time, and input needs to
 *      resolve to a time before it.
 *
 * @param inputStr     user date/time input in any of a wide variety of formats
 * @param refTime      reference timestamp
 * @param nowTime      current time (usually for testing)
 * @param timezone     timezone to use, if null uses local timezone
 *
 * @returns {any} object containing the input and the parsed date/time in various formats:
 *
 *     {number} milliseconds    time in ms
 *     {string} nanoseconds     time in ns
 *     {Moment} moment          time as a moment
 *     {Moment} ref             reference time as moment (for relative times)
 *
 * The 'nanoseconds' field is only present if necessary (the input provided nanoseconds).
 *
 * To see what can and cannot be parsed, see time.test.js.
 *
 * Note: any test that calls into this function should set 'now' in order to get predictable results.
 *
 * TODO: Validation. Currently very forgiving, will return unpredictable results for random strings of numbers, for
 * TODO: example '123455' or '1234556677' or '1 2 3 4 5 6 7'.
 * TODO: Would it be easier to just use a bunch of regexes?
 */

// #region util functions for the `parseDateTime()`

// removes the matched portion of the input
export function updateInput(input: string, match: RegExpExecArray | null): string {
  if (!match) {
    return input;
  }
  return (input.substr(0, match.index) + input.substr(match.index + match[0].length)).trim();
}

// #endregion

export const parseDateTime = (
  inputStr = '',
  refTime: moment.Moment | null = null,
  nowTime: moment.Moment | null = null,
  timezone: string | null,
): ParsedDateTime | null => {
  let input = inputStr;

  // do this first to avoid clock skew
  const now = nowTime ? timezoneMoment(timezone)(nowTime) : timezoneMoment(timezone)();

  // make sure that given date comes before ref, subtracting unit as many times as necessary
  function normalize(
    date: moment.Moment,
    num = 1,
    unit: moment.DurationInputArg2 | null,
    ref: moment.Moment,
    grace = 0,
  ): moment.Moment {
    if (!date || !unit) {
      return date;
    }
    while (moment.max(date, ref) === date && date.valueOf() - ref.valueOf() > grace) {
      date.subtract(num, unit);
    }
    return date;
  }

  // take a moment and use it to fill out the result object
  // an optional fractional second will affect ms and possibly result in nanoseconds
  function returnValue(m: moment.Moment, fraction: string | null = null, ref1?: moment.Moment): ParsedDateTime {
    const result: ParsedDateTime = {
      moment: m,
      milliseconds: m.valueOf(),
      nanoseconds: '',
    };
    if (fraction) {
      m.milliseconds(Number(fraction.substr(0, 3).padEnd(3, '0')));
      result.milliseconds = m.valueOf();
      if (fraction.length > 3) {
        // if we get more than 3 digits, preserve as ns
        result.nanoseconds = String(result.milliseconds) + fraction.substr(3).padEnd(6, '0');
      }
    }
    result.ref = ref1;

    return result;
  }

  // returns true if input is a reasonable four-digit year
  function isYear(input1: string | number): boolean {
    const num = Number(input1);
    return num >= 2000 && num <= 2100;
  }

  // converts 0-12 hour with possible am/pm to 0-24 hour
  function resolveHour(hour: number, amPm: string | undefined, timeOnly = false): number {
    if (amPm === 'pm' && hour < 12) {
      return hour + 12;
    }
    if (amPm === 'am' && hour === 12) {
      return 0;
    }

    // If we have hour < 12 and no date, assume it's after noon if our current time is after it (and also after noon).
    // For example, if we get a 3 and it's now 4:00 pm, interpret the 3 as 3:00 pm.
    const nowHour = now.hour();
    if (timeOnly && !amPm && hour > 0 && hour < 12 && nowHour > 12 && hour <= nowHour - 12) {
      return hour + 12;
    }

    return hour;
  }

  // no input
  if (input == null || input === '') {
    return returnValue(now);
  }

  input = ('' + input).trim();

  // ISO 8601 - look for T, see if moment likes it
  //
  // Example: 2017-10-11T11:45:00+0800
  let match: RegExpExecArray | null = /^[\d-]+T[\d:]+\.?(\d*)/.exec(input);
  if (match) {
    const m = timezoneMoment(timezone)(input);
    if (m && m.isValid()) {
      return returnValue(m, match[1]);
    }
  }

  input = input.toLowerCase(); // all weekdays, month names, and am/pm are handled as lower case

  if (input === 'now') {
    return returnValue(now);
  }

  // ignore: _ ,
  input = input.replace(/[,_]/g, ' ');

  // unix, ms, or ns epoch timestamp
  //
  // Example: 1346261004000 ms
  // Example: 1346261004000
  // Example: 1543539714
  // Example: 13462610040000148732
  match = /^(\d+)\s*(ms|ns)?$/.exec(input);
  const str = match && match[1];
  const len = str && str.length;
  let m: moment.Moment;
  // unix epoch in seconds
  if (len === 10) {
    return returnValue(moment.unix(Number(str)));
  }
  // unix epoch in milliseconds
  if (len === 13) {
    return returnValue(timezoneMoment(timezone)(Number(str)));
  }
  // unix epoch in nanoseconds
  if (len === 19 && str) {
    const ms = Number(str.substr(0, 13));
    if (str.substr(-6) === SIX_ZEROS) {
      // ns with ms precision; just return ms
      return returnValue(timezoneMoment(timezone)(ms));
    }
    // moment doesn't support nanoseconds; break into ms plus fractional ns
    return returnValue(timezoneMoment(timezone)(ms), str.substr(-9));
  }

  // checks for invalid digit-only non-epoch inputs (e.g. FRONT-4337 Corner case with "11111" input)
  if (
    input.length > 2 && // '1' or '10' is a valid input (hours)
    !isEpochTime(input) &&
    REGEXP.digits.test(input) &&
    input.length !== 6 &&
    input.length !== 8
  ) {
    return null;
  }

  // relative time (duration)
  //
  // Example: 4d
  // Example: +1.5 hours
  match = REGEXP.duration.exec(input);
  const durationAmount = match ? match[1] : '';
  const durationUnit = match ? DURATION_UNIT[match[2]] : '';
  if (durationUnit) {
    let num: moment.DurationInputArg1 = Number(durationAmount);
    let unit: moment.DurationInputArg2 = durationUnit as moment.DurationInputArg2;
    // momentjs ignores fraction for days/months; we just support days
    const frac = unit === K.DATE.DAY && num - Math.floor(num) > 0;
    if (frac) {
      unit = K.DATE.HOUR;
      num *= 24;
    }
    // momentjs will handle fractional second up to ms, but we need to handle ns
    let fracSec: string | null = durationAmount.split('.')[1];
    fracSec = fracSec && fracSec.length > 3 ? fracSec : null;
    // duration relative to start
    if (input[0] === '+' && refTime) {
      const ref: moment.Moment = timezoneMoment(timezone)(refTime);
      return returnValue(ref.add(num, unit), fracSec);
    }
    // duration relative to now
    return returnValue(now.subtract(num, unit), fracSec, now);
  }

  // at this point, we need to parse piece by piece; start with most identifiable pieces; remove pieces as we find them

  // normalization unit
  let unit: moment.DurationInputArg2 | null = null;

  // accumulate date/time components
  const comps: Record<K.DATE, string | number> = {} as Record<K.DATE, string | number>;

  // UTC offset (only appears at the end); will be passed to moment's utcOffset() function, which accepts string
  match = /[a-z]+$/.exec(input);
  let offset = '';
  const isOffset = match && IS_OFFSET[match[0]];
  if (isOffset) {
    offset = '0'; // utc
  } else {
    match = /\s+([+-]\d\d\d\d)$/.exec(input);
    if (match) {
      offset = match[1];
    }
  }
  if (offset && match) {
    input = input.substr(0, input.length - (isOffset ? match[0].length : offset.length)).trim();
  }

  let fraction;
  let gotDate = false;

  // look for time; must either have ':' or am/pm
  if (input.indexOf(':') !== -1 || input.indexOf('am') !== -1 || input.indexOf('pm') !== -1) {
    let gotTime = false;
    // first see if we have a colon-separated time
    match = REGEXP.relativeTimeColonSeparated.exec(input);
    if (match) {
      const timeMatch = REGEXP.timeMilitary.exec(input); // no easy way to grab just the time portion

      let parts: string[] = [];
      if (timeMatch) {
        parts = timeMatch[0].split(':');
      }

      let hour = -1;
      if (match[3] || parts.length > 1) {
        hour = Number(parts[0]);
      }
      if (hour >= 0 && hour <= 23) {
        comps[K.DATE.HOUR] = resolveHour(hour, match[3], match[0].length === input.length);

        comps[K.DATE.MINUTE] = 0;
        if (parts[1] != null) {
          comps[K.DATE.MINUTE] = Number(parts[1]);
        }

        comps[K.DATE.SECOND] = 0;
        if (parts[2] != null) {
          comps[K.DATE.SECOND] = Number(parts[2]);
        }

        if (match[2]) {
          fraction = match[2].substr(1);
        }

        input = updateInput(input, match);
        unit = K.DATE.DAY;
        gotTime = true;
      }
    }
    // check for time with no colon - an hour and am/pm, eg "6 am"; parsing depends on number of digits (from 1 to 6):
    // H HH HMM HHMM HMMSS HHMMSS
    if (!gotTime) {
      match = REGEXP.timeRegular.exec(input);
      if (match) {
        const num = match[1];
        let hour = 0;
        let minutes = 0;
        let seconds = 0;
        if (num.length <= 2) {
          hour = Number(num);
        } else if (num.length === 3) {
          hour = Number(num.substring(0, 1));
          minutes = Number(num.substring(1));
        } else if (num.length === 4) {
          hour = Number(num.substring(0, 2));
          minutes = Number(num.substring(2));
        } else if (num.length === 5) {
          hour = Number(num.substring(0, 1));
          minutes = Number(num.substring(1, 3));
          seconds = Number(num.substring(3));
        } else if (num.length === 6) {
          hour = Number(num.substring(0, 2));
          minutes = Number(num.substring(2, 4));
          seconds = Number(num.substring(4));
        }
        comps[K.DATE.HOUR] = resolveHour(hour, match[2]);
        comps[K.DATE.MINUTE] = minutes;
        comps[K.DATE.SECOND] = seconds;
        input = updateInput(input, match);
        unit = K.DATE.DAY;
        gotTime = true;
      }
    }
  }

  // date consisting of year, month, and day separated by / or - or . OR month and day separated by /
  // must be MDY, DMY, YMD, MD, or DM
  match = REGEXP.yearMonthDay.exec(input) || REGEXP.monthDay.exec(input);
  if (match) {
    const [one, two, three] = [match[1], match[3], match[4]].map((part) => Number(part));
    if (isYear(one) || (!isYear(three) && one > 12)) {
      // year first
      comps[K.DATE.YEAR] = one < 2000 ? one + 2000 : one;
      comps[K.DATE.MONTH] = two - 1;
      comps[K.DATE.DAY] = three;
      gotDate = true;
    } else {
      // year last, or not present
      if (three) {
        comps[K.DATE.YEAR] = three < 2000 ? three + 2000 : three;
      }
      // Note: momentjs months are zero-based
      comps[K.DATE.MONTH] = one <= 12 ? one - 1 : two - 1;
      comps[K.DATE.DAY] = one <= 12 ? two : one;
      // handle European date format (day then month) when it's ambiguous (FRONT-7099)
      if (one <= 12 && two <= 12 && one !== two) {
        const curMonth = now.month() + 1;
        const deltaOne = curMonth - one;
        const deltaTwo = curMonth - two;
        if (deltaOne < 0 || deltaTwo < 0) {
          // at least one is in future, take earliest
          comps[K.DATE.MONTH] = deltaOne >= deltaTwo ? one - 1 : two - 1;
          comps[K.DATE.DAY] = deltaOne >= deltaTwo ? two : one;
        }
      }
      gotDate = true;
    }
    if (gotDate) {
      input = updateInput(input, match);
    }
  }

  // now that we've looked for offset, time, and date, it's safe to ignore hyphens, slashes, and periods
  input = input.replace(/[-./]/g, ' ');

  // at this point we should be left with a space-separated date and/or a single-number time (hour), and possibly a day of the week

  // pull out day of week
  match = REGEXP.dayOfWeek.exec(input);
  const weekday = match && match[0];
  if (weekday) {
    input = updateInput(input, match);
  }

  let tokens: Array<string | number> = input.split(/\s+/);
  const numTokens = tokens.length;

  // any remaining letters should be month
  match = (!gotDate && /[a-z]+/.exec(input)) || null;
  if (match) {
    const monthPos = tokens.indexOf(match[0]);
    const month = MONTH_NUM.indexOf(match[0].substr(0, 3));
    if (month !== -1) {
      comps[K.DATE.MONTH] = month;
      // convert all non-month tokens to numbers
      tokens = tokens.map((token: string | number, index: number) => (index !== monthPos ? Number(token) : token));
      if (numTokens === 2) {
        // 'mar 8', '8 mar'
        comps[K.DATE.DAY] = monthPos === 0 ? tokens[1] : tokens[0];
        gotDate = true;
        unit = K.DATE.YEAR;
      } else if (numTokens === 3) {
        // 'mar 8 2011', '8 mar 2011', '2011 8 mar', 'mar 8 11', '8 mar 11', '11 8 mar'
        if (isYear(tokens[0])) {
          comps[K.DATE.YEAR] = tokens[0];
          comps[K.DATE.DAY] = tokens[2];
        } else if (isYear(tokens[2])) {
          comps[K.DATE.YEAR] = tokens[2];
          comps[K.DATE.DAY] = monthPos === 0 ? tokens[1] : tokens[0];
        } else if (monthPos === 2) {
          comps[K.DATE.DAY] = tokens[1];
          comps[K.DATE.HOUR] = tokens[0];
        } else {
          comps[K.DATE.DAY] = monthPos === 0 ? tokens[1] : tokens[0];
          comps[K.DATE.HOUR] = tokens[2];
        }
        gotDate = true;
      } else if (numTokens === 4) {
        // '11 mar 8 2017', '11 8 mar 2017', '2017 8 mar 11', 'mar 8 2017 11', '8 mar 2017 11'
        let yearPos;
        tokens.forEach((token: string | number, index: number) => {
          if (isYear(token)) {
            yearPos = index;
          }
        });
        if (yearPos != null) {
          comps[K.DATE.YEAR] = tokens[yearPos];
          if (Math.abs(monthPos - yearPos) === 2) {
            const datePos = Math.max(monthPos, yearPos) - 1;
            comps[K.DATE.DAY] = tokens[datePos];
            comps[K.DATE.HOUR] = yearPos === 3 ? tokens[0] : tokens[3];
          } else {
            comps[K.DATE.HOUR] = yearPos === 3 ? tokens[0] : tokens[3];
            comps[K.DATE.DAY] = yearPos === 3 ? tokens[1] : tokens[0];
          }
          gotDate = true;
        }
      }

      if (gotDate) {
        input = '';
      }
    } else {
      return null; // in case we can't identify the month – input is invalid: e.g. '2 years' or '2 bears'
    }
  }

  if (!gotDate) {
    // parse remainder as string of digits; zero-pad any lone digit
    input = input
      .split(/\s+/)
      .map((x) => (x.length === 1 ? '0' + x : x))
      .join('')
      .replace(/\D/g, '');
  }

  // string of 2, 4, 6, or 8 digits
  if (/^\d+$/.test(input)) {
    if (input.length === 8) {
      // 20180814 07182008 25032016
      // find year and pull it out
      const digits = input.match(/.{1,4}/g);
      if (digits) {
        const parts = digits.map((part) => Number(part));
        const yearFirst = isYear(parts[0]);
        comps[K.DATE.YEAR] = yearFirst ? parts[0] : parts[1];
        input = yearFirst ? input.substr(4) : input.substr(0, 4);
      }
    } else {
      let parts: number[] = [];
      const digits = input.match(/.{1,2}/g);
      if (digits) {
        parts = digits.map((part) => Number(part));
      }
      if (parts.length === 1) {
        // length is 2 (just the hour)
        comps[K.DATE.HOUR] = resolveHour(parts[0], undefined, !gotDate);
        input = '';
      } else if (parts.length === 3) {
        // length is 6 - parse as date; 112317 180426
        const yearFirst = parts[0] > 12;
        comps[K.DATE.YEAR] = yearFirst ? parts[0] + 2000 : parts[2] + 2000;
        input = yearFirst ? input.substr(2) : input.substr(0, 4);
      }
    }
    // at this point we have mmdd or ddmm
    if (input) {
      const digits = input.match(/.{1,2}/g);
      if (digits) {
        const parts = digits.map((part) => Number(part));
        comps[K.DATE.MONTH] = parts[0] <= 12 ? parts[0] - 1 : parts[1] - 1;
        comps[K.DATE.DAY] = parts[0] <= 12 ? parts[1] : parts[0];
        gotDate = true;
      }
    }
  }

  m = now.clone();

  // apply weekday if we don't have a day
  if (weekday && !gotDate && !comps[K.DATE.DAY]) {
    let wday = DAY_NUM.indexOf(weekday.substring(0, 3));
    if (wday > now.day()) {
      wday = wday - 7; // in Moment, a negative value for weekday goes back in time
    }
    comps[K.DATE.WEEKDAY] = wday;
    unit = K.DATE.WEEK;
  }

  // adjust by offset if we got one
  if (offset) {
    // m = moment.utc(comps).utcOffset(offset);
    m = moment.utc().utcOffset(offset);
  }

  // apply components in the proper order (increasing granularity)
  let gotTerm = false;
  let isInvalid = false;
  const dateParts = [
    K.DATE.YEAR,
    K.DATE.MONTH,
    K.DATE.WEEK,
    K.DATE.DAY,
    K.DATE.WEEKDAY,
    K.DATE.HOUR,
    K.DATE.MINUTE,
    K.DATE.SECOND,
    K.DATE.MILLISECOND,
  ];
  dateParts.forEach((term): void => {
    if (comps[term] != null) {
      const range = UNIT_RANGE[term];
      if (comps[term] < range.min || comps[term] > range.max) {
        isInvalid = true;
      } else {
        const setter = MOMENT_UNIT[term] as K.DATE; // odd that this cast works, but it clears TS warning
        m[setter](comps[term]);
        gotTerm = true;
      }
    }
  });

  if (isInvalid) {
    return null;
  }

  // time components default to 00 if not provided
  if (gotTerm) {
    [K.DATE.HOUR, K.DATE.MINUTE, K.DATE.SECOND, K.DATE.MILLISECOND].forEach((term) => {
      if (comps[term] == null) {
        m[term](0);
      }
    });
  } else {
    return null;
  }

  // allow for dates up to 30 days in future if we have a date
  let grace;
  if (comps[K.DATE.MONTH] && comps[K.DATE.DAY]) {
    grace = 1000 * 60 * 60 * 24 * 30;
  }
  // and for up to a week in the future if we have a weekday
  if (comps[K.DATE.WEEKDAY]) {
    grace = 1000 * 60 * 60 * 24 * 7;
  }
  m = normalize(m, 1, unit, now, grace);

  // FRONT-6167: if we get start/end straddling current time, move start back so it's before end (the refTime)
  if (refTime && comps[K.DATE.HOUR] && !comps[K.DATE.DAY]) {
    m = normalize(m, 12, 'hour', refTime);
  }

  return returnValue(m, fraction);
};

/**
 * Converts a duration (such as "4 hours") into a number of ms. Strips a leading + if present.
 *
 * @param input    duration
 *
 * @returns ms in duration
 */
export const parseDuration = (input: string): number => {
  const match = REGEXP.duration.exec(input.replace(/^\+/, ''));
  if (match) {
    const durationAmount = match[1];
    const durationUnit = DURATION_UNIT[match[2]];
    if (durationAmount && durationUnit) {
      const num = Number(match[1]);
      return num * MS_PER_UNIT[durationUnit];
    }
  }
  return -1;
};

/**
 * Normalizes duration text from any of the formats we support into its full form, eg '3d' => '3 days'.
 * Strips a leading + if present.
 *
 * @param input    duration
 *
 * @returns normalized duration text
 */
export const normalizeDuration = (input: string): string => {
  const match = REGEXP.duration.exec(input.replace(/^\+/, ''));
  if (match) {
    const durationAmount = Number(match[1]);
    const durationUnit = DURATION_UNIT[match[2]];
    if (durationAmount && durationUnit) {
      return `${String(durationAmount)} ${durationUnit}${durationAmount > 1 ? 's' : ''}`;
    }
  }

  return '';
};

/**
 * Returns true if the given string is a duration. Examples: '2 days' '4h' '+35m'
 *
 * @param input    start or end time
 *
 * @returns true if the input is a duration
 */
export const isDuration = (input: string): boolean => parseDuration(input) !== -1;

/**
 * Returns true if the given string requires the timezone to determine it's absolute value. For example, the value of
 * '10am' changes when the user sets a new timezone, but the values of '1630687226000' and '4h' do not.
 *
 * @param input    start or end time
 *
 * @returns true if the input's value depends on the user's timezone
 */
export const isTimezoneContingent = (input: string): boolean => !!input && !isDuration(input) && !isEpochTime(input);

/**
 * Parse the two values of a time range.
 *
 * If the end time starts with a '+', then it is relative to the start and we need to parse start first.
 *
 * @param range
 * @param timezone timezone to use, if null uses local timezone
 * @param now
 * @returns
 */
export const parseTimeRange = (
  range: TimeRange,
  timezone: string | null,
  now?: moment.Moment,
): {
  startTime: ParsedDateTime | null;
  endTime: ParsedDateTime | null;
} => {
  let startTime = null;
  let endTime = null;

  if (range.endTime && isDuration(range.endTime) && range.endTime.startsWith('+')) {
    startTime = parseDateTime(range.startTime, now, now, timezone);
    endTime = parseDateTime(range.endTime, startTime?.moment, now, timezone);
  } else {
    endTime = range.endTime
      ? parseDateTime(range.endTime, null, now, timezone)
      : parseDateTime('', null, now, timezone);
    startTime = parseDateTime(range.startTime, endTime?.moment, now, timezone);
  }

  return { startTime, endTime };
};

/**
 * Convert start and end to Unix epoch millisecond representations.
 *
 * @param range    a time range
 * @param timezone timezone to use, if null uses local timezone
 * @param now      current time (for testing)
 *
 * @returns object with start and end in ms (or -1 for any that can't be parsed)
 */
export const convertTimeRangeToMs = (range: TimeRange, timezone: string | null, now?: moment.Moment): TimeRange => {
  const parsedRange = parseTimeRange(range, timezone, now);
  const startTimeMs = parsedRange?.startTime?.milliseconds;
  const endTimeMs = parsedRange?.endTime?.milliseconds;

  return {
    startTime: startTimeMs ? String(startTimeMs) : '',
    endTime: endTimeMs ? String(endTimeMs) : '',
  };
};

/**
 * Convert start and end to ISO-8601 representations.
 *
 * @param range    a time range
 * @param timezone timezone to use, if null uses local timezone
 * @param now      current time (for testing)
 *
 * @returns object with start and end in ms (or -1 for any that can't be parsed)
 */
export const convertTimeRangeToIso = (range: TimeRange, timezone: string | null, now?: moment.Moment): TimeRange => {
  const parsedRange = parseTimeRange(range, timezone, now);

  return {
    startTime: parsedRange?.startTime?.moment.toISOString() || '',
    endTime: parsedRange?.endTime?.moment.toISOString() || '',
  };
};

/**
 * Return the difference in ms between the two given times, or -1 if either time is missing or could not be parsed.
 *
 * @param range    a time range
 * @param timezone timezone to use, if null uses local timezone
 * @param now      current time (for testing)
 *
 * @return the time difference in ms
 */
export const getTimeDifferenceInMs = (range: TimeRange, timezone: string | null, now?: moment.Moment): number => {
  if (!range.startTime) {
    return -1;
  }
  const timeRange = convertTimeRangeToMs(range, timezone, now);

  if (timeRange.startTime && timeRange.endTime) {
    return Number(timeRange.endTime) - Number(timeRange.startTime);
  }

  return -1;
};

/**
 * Get text for the given duration, in terms of the largest applicable time unit. The unit can be overridden to force
 * display in that unit. Fractions will be rounded to the nearest integer. A duration of zero will be expressed in
 * seconds. If a string is provided, the only change is to convert the units to a word, for example '36h' will
 * become '36 hours'.
 *
 * NOTE: We can't just do moment.duration(ms).humanize() because it rounds to higher unit, eg 36h becomes '2 days'
 *
 * @param input        length of duration in ms, or a string duration such as '36h'
 * @param unitToUse    [optional] duration unit to use
 * @param doRound      if true, round to a whole integer rather than a single decimal place
 *
 * @returns duration text
 */
export const getDurationText = (input?: string | number, unitToUse?: K.DATE, doRound = false): string => {
  if (input == null) {
    return 'no time';
  }

  // get duration as milliseconds and figure what unit we should convert it to
  let ms = 0;
  let unit = unitToUse;
  if (isString(input)) {
    const match = REGEXP.duration.exec(input);
    if (match) {
      const durationUnit = DURATION_UNIT[match[2] as K.DATE];
      if (durationUnit) {
        ms = Number(match[1]) * MS_PER_UNIT[durationUnit];
        unit = unit || durationUnit;
      }
    }
  } else {
    ms = Number(input);
  }

  // if no unit was provided, use the largest one we can
  const units = [K.DATE.YEAR, K.DATE.MONTH, K.DATE.WEEK, K.DATE.DAY, K.DATE.HOUR, K.DATE.MINUTE, K.DATE.SECOND]; // descending order by size
  let found = !!unit;
  if (!found) {
    for (let i = 0; i < units.length; i += 1) {
      unit = units[i];
      if (ms / MS_PER_UNIT[unit] >= 1) {
        found = true;
        break;
      }
    }
  }

  // convert ms into unit, rounding if desired
  if (unit) {
    const unitText = DURATION_UNIT[unit];
    if (unitText && found) {
      const rawNum = Number(ms / MS_PER_UNIT[unit]);
      let num = doRound ? Math.round(rawNum) : Number(rawNum.toFixed(1));
      const unitName = unitText + (num !== 1 ? 's' : ''); // pluralize
      num = Math.floor(num) === num ? Math.floor(num) : num; // strip trailing .0
      return num + ' ' + unitName;
    }
  }

  return ms + (ms > 0 ? ' milliseconds' : ' seconds');
};

/**
 *
 * Get prettified(optionally) text for a given timestamp, possibly in reference to another time(for testing)
 *
 * @param timezone timezone to use, if null uses local timezone
 * @param strictFormatting   if set to `true` will format in moment identifiable way (i.e. excluding such visual enhancements as `&middot`)
 *
 */
export const getDateString = (
  date: moment.Moment,
  refDate: moment.Moment | null,
  useSeconds: boolean,
  timezone: string | null,
  now?: moment.Moment,
  strictFormatting = false,
): string => {
  const nowMoment = now || timezoneMoment(timezone)();

  // return true if the two moments occur on the same day
  const isSameDay = (a: moment.Moment, b: moment.Moment): boolean =>
    a.year() === b.year() && a.dayOfYear() === b.dayOfYear();

  if (!date) {
    return '';
  }

  const timeFormat = useSeconds ? 'h:mm:ss a' : 'h:mm a';
  const dayFormat = useSeconds ? 'MMM D * h:mm:ss a' : 'MMM D * h:mm a';
  const isToday = isSameDay(date, nowMoment);
  const refIsToday = !refDate || isSameDay(refDate, nowMoment);

  if (strictFormatting) {
    return date.format(dayFormat).replace('* ', '');
  }

  if (isToday && !refIsToday) {
    return 'Today ' + date.format(timeFormat);
  }

  if (!isToday) {
    return date.format(dayFormat).replace('*', '\u00b7'); // replace * with &middot;
  }

  return date.format(timeFormat);
};

/**
 * Generate a human-readable time range from the given start and end timestamps. The range will be expressed in
 * terms of the largest available unit, eg "Last 3 days" instead of "Last 72 hours". The range can either be
 * between two specific timestamps or it can be duration-based. A time will show seconds if the range covers
 * less than 500 seconds.
 *
 * @param range       a time range
 * @param timezone    timezone to use, if null uses local timezone
 * @param now         current time (for testing)
 *
 * @returns a readable time range
 */
export const getTimeRangeText = (range: TimeRange, timezone: string | null, now?: moment.Moment): string => {
  const start = range.startTime;
  let end = range.endTime || '';
  const nowMoment = now || timezoneMoment(timezone)();

  let endSuffix = '';

  // end relative to start, as a duration (eg '+20m' means 20 minutes after start time)
  if (end.startsWith('+')) {
    endSuffix = ' later';
    end = end.substring(1);
  }

  // 'now' is the default when end is unspecified
  if (end === 'now') {
    end = '';
  }

  // duration is something like '4h' or '10 minutes'; function returns number of ms for a duration, -1 otherwise
  const startDuration = normalizeDuration(start);
  const endDuration = normalizeDuration(end);

  // start time is relative, end defaults to now
  if (startDuration && !end) {
    return 'Last ' + startDuration;
  }

  let startText;
  let endText;
  if (startDuration) {
    startText = startDuration + ' ago';
  }
  if (endDuration) {
    endText = endDuration + (endSuffix || ' ago');
  }

  const { startTime: startResult, endTime: endResult } = parseTimeRange(range, timezone, now);
  if (!startResult || !endResult) {
    return '';
  }

  const startTime = startResult.milliseconds;
  const endTime = endResult.milliseconds;
  const useSeconds = endTime - startTime < MAX_SECONDS_DURATION * MS_PER_UNIT[K.DATE.SECOND];

  startText =
    startText || getDateString(startResult.moment, endResult && endResult.moment, useSeconds, timezone, nowMoment);
  if (!endText) {
    endText = !end
      ? 'now'
      : getDateString(endResult && endResult.moment, startResult.moment, useSeconds, timezone, nowMoment);
  }

  return [startText, endText].join(' - ');
};

/**
 * Converts different values: either time values from log entry or other, to timestamp.
 *
 * @param time  some representation of timestamp.
 */
export const convertTimestampToMilliseconds = (time: number | string): number => {
  // Valid till 2286-11-20 5:46:40 PM.
  const MILLISECONDS_BASED_TIMESTAMP_LENGTH = 13;

  return Number(
    String(time).substring(0, MILLISECONDS_BASED_TIMESTAMP_LENGTH).padEnd(MILLISECONDS_BASED_TIMESTAMP_LENGTH, '0'),
  );
};

// TODO: FRONT-4337 Technical Debt, cover getReadableTimeRange() cases with tests (100% coverage recommended)
/**
 * Convert epoch start/end to readable times. Start/end that are not epoch times are returned as is.
 *
 * @param range       a time range
 * @param timezone    timezone to use, if null uses local timezone
 * @param now         current time (for testing)
 *
 * @returns a readable time range
 */
export const getReadableTimeRange = (range: TimeRange, timezone: string | null, now?: moment.Moment): TimeRange => {
  const nowMoment = now || timezoneMoment(timezone)();

  const { startTime: start, endTime: end } = range;
  const startIsEpoch = isEpochTime(start);
  const endIsEpoch = end && isEpochTime(end);

  // neither is epoch, return unchanged
  if (!startIsEpoch && !endIsEpoch) {
    return range;
  }

  const startDate = new Date(convertTimestampToMilliseconds(start));
  const endDate = new Date(convertTimestampToMilliseconds(end ?? Date.now()));
  const startDateMoment = timezoneMoment(timezone)(startDate);
  const endDateMoment = timezoneMoment(timezone)(endDate);

  const useSeconds = startDateMoment.seconds() > 0 || endDateMoment.seconds() > 0;

  // one is epoch; convert it to text
  return {
    startTime: !startIsEpoch ? start : getDateString(startDateMoment, null, useSeconds, timezone, nowMoment, true),
    endTime: !endIsEpoch || !end ? end : getDateString(endDateMoment, null, useSeconds, timezone, nowMoment, true),
  };
};

/**
 * Validate if given input string matches time format. Supports anything supported by parseDateTime().
 *
 * @param input      input string for validation
 *
 * @returns true if input is a valid date/time
 */
export const validateTimeInput = (input: string): boolean => parseDateTime(input, null, null, null) !== null;

export type QueryTimeRange = {
  startTime: string;
  endTime: string;
  startTimeNs: string;
  endTimeNs: string;
  requestTime: string;
};

/**
 * Get the current time in milliseconds by using the custom `nowTime` parameter if provided or else the local machine
 * time. Then, use the current time to resolve the time range from the provided `startTime` and `endTime` parameters,
 * and generate the request time by rounding the current time to the nearest second.
 *
 * @param timezone           timezone to use, if null uses local timezone
 * @param startTime          startTime parameter from the url
 * @param endTime            endTime parameter from the url
 * @param defaultStart       the default start time from configuration
 * @param nowTime            current time (for testing)
 *
 * @returns                  object that merges the resolved time range with the request time
 */
export const generateQueryTimeRange = (
  timezone: string | null,
  startTime?: string,
  endTime?: string,
  defaultStart?: string,
  nowTime?: moment.Moment,
): QueryTimeRange => {
  const now = nowTime || timezoneMoment(timezone)();

  // calculate the time that the request was generated to 10 significant figures, which is accurate to the second
  const requestTime = String(roundDownToSigFigs(now.valueOf(), 10));
  let timeRange: TimeRange = {
    startTime: startTime || defaultStart || K.DEFAULT_SEARCH_START,
    endTime,
  };

  // resolve start/end to absolute millisecond and nanosecond timestamps
  const parsedRange = parseTimeRange(timeRange, timezone, now);
  const startTimeMs = String(parsedRange?.startTime?.milliseconds || '');
  const endTimeMs = String(parsedRange?.endTime?.milliseconds || '');
  const queryTimeRange = {
    requestTime,
    startTime: startTimeMs,
    endTime: endTimeMs,
    startTimeNs: parsedRange?.startTime?.nanoseconds || (startTimeMs ? startTimeMs + SIX_ZEROS : ''),
    endTimeNs: parsedRange?.endTime?.nanoseconds || (endTimeMs ? endTimeMs + SIX_ZEROS : ''),
  } as QueryTimeRange;

  return queryTimeRange;
};

/**
 * Shift the given time range back or forward by the given factor. End time is capped at current time.
 *
 * @param range     time range
 * @param back      if true (default), shift backward in time
 * @param factor    proportion of the given range to shift (defaults to 1)
 * @param now       current time (for testing)
 */
export const shiftTimeRange = (
  range: TimeRange,
  back = true,
  factor = 1,
  timezone: string | null,
  now?: moment.Moment,
): TimeRange => {
  const nowMoment = now || timezoneMoment(timezone)();

  const startTime = parseDateTime(range.startTime, null, nowMoment, timezone);
  const endTime = parseDateTime(range.endTime, null, nowMoment, timezone);
  const startMs = startTime?.milliseconds;
  const endMs = endTime?.milliseconds;

  let newStartTime = 0;
  let newEndTime = 0;
  if (startMs && endMs) {
    const shiftMs = (endMs - startMs) * factor;
    newStartTime = back ? startMs - shiftMs : startMs + shiftMs;
    newEndTime = back ? endMs - shiftMs : endMs + shiftMs;
  }

  if (newStartTime && newEndTime) {
    return {
      startTime: String(newStartTime),
      endTime: String(Math.min(newEndTime, nowMoment.valueOf())),
    };
  }

  return range;
};

/**
 * Formats a timestamp in Scalyr's preferred style, e.g.: Jan 13 · 9:42:55.432 am
 *
 * @param value    A number to be formatted as a timestamp
 * @param timezone optional timezone for timezone offsetted formatting
 *
 * @returns timestamp as a formatted string
 */
export const formatTimestamp = (value: number, timezone: string | null): string =>
  timezoneMoment(timezone)(convertTimestampToMilliseconds(value)).format('MMM D · h:mm:ss.SSS a');

/**
 * Formats a timestamp in  style, e.g.: 07/23/92
 *
 * @param value    A number to be formatted as a timestamp
 * @param timezone optional timezone for timezone offsetted formatting
 *
 * @returns timestamp as a formatted string
 */
export const formatMonthDayYear = (value: number, timezone: string | null): string =>
  timezoneMoment(timezone)(convertTimestampToMilliseconds(value)).format('MM/DD/ֿֿYY');

/**
 * Returns an ISO-formatted date (at the start) for a given number of days ago.
 *
 * @param numBack    number of days ago for the date
 * @param now        current time (for testing)
 */
export function getPreviousDay(numBack: number, now?: moment.Moment): string {
  return moment(now).subtract(numBack, 'day').startOf('day').toISOString();
}

/**
 * Returns an ISO-formatted date for today (at the start)
 *
 * @param now    current time (for testing)
 */
export function getToday(now?: moment.Moment): string {
  return getPreviousDay(0, now);
}

/**
 * Returns an ISO-formatted date (at the start) for the first day of the current month.
 *
 * @param now    current time (for testing)
 */
export function getFirstDayOfMonth(now?: moment.Moment): string {
  return moment(now).startOf('month').startOf('day').toISOString();
}

/**
 * Returns an ISO-formatted date rounded up to the next hour.
 *
 * @param now    current time (for testing)
 */
export function getNextHour(now?: moment.Moment): string {
  return moment(now).add(1, 'hour').startOf('hour').toISOString();
}

/**
 * Returns a nanosecond string representation of a millisecond-precision timestamp with some number of milliseconds
 * added.
 *
 * Nanosecond string is the preferred format for sending timestamps to the server, but we do not attempt
 * computations on nanoseconds in the client.
 *
 * Note that {@see convertTimestampToMilliseconds} truncates nanosecond inputs.
 *
 * @param timestamp       the timestamp to modify
 *                        may be a string or number representation of a millisecond or nanosecond timestamp
 * @param milliseconds    some integer number of milliseconds
 *
 * @returns               nanosecond timestamp as a string
 */
export function addMillisecondsToTimestamp(timestamp: number | string, milliseconds: number): string {
  const sum = convertTimestampToMilliseconds(timestamp) + milliseconds;
  return String(sum) + SIX_ZEROS;
}

/**
 * Returns a ParsedDateTime from a user input string. This function is like parseDateTime, except that it is
 * looking for the nearest future matching time or duration, instead of the past.
 *
 * @param inputStr     representation of future time or future duration
 * @param timezone     timezone to use, if null uses local timezone
 * @param nowTime      current time (usually for testing)
 *
 * @returns            ParsedDateTime in the near future
 */
export function parseFutureDateTime(
  inputStr: string,
  timezone: string | null,
  nowTime: moment.Moment | null = null,
): ParsedDateTime | null {
  const now = nowTime ? timezoneMoment(timezone)(nowTime) : timezoneMoment(timezone)();
  let refTime = null;
  let adjustedNowTime = null;

  // for durations, we can use a refTime and the '+' syntax to make sure it is in the future
  if (isDuration(inputStr)) {
    inputStr = inputStr.startsWith('+') ? inputStr : `+${inputStr}`;
    refTime = now;
  } else {
    // if we find a match on weekday (such as "Mon"), parse as if we are a week in the future
    if (REGEXP.dayOfWeek.test(inputStr.toLowerCase())) {
      adjustedNowTime = now.add(1, 'week');
    } else {
      // otherwise, parse as if we are a day in the future
      adjustedNowTime = now.add(1, 'day');
    }
  }
  return parseDateTime(inputStr, refTime, adjustedNowTime, timezone);
}
